import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { getFranchiseeById, editFranchisee, createXeroContactFromFranchisee, previewXeroContactFromFranchisee, updateFranchiseeCompliance, getXeroBankDetailsStatus, syncXeroBankDetails } from '../services/franchiseeService';
import { uploadFiles, uploadSingleFile, uploadCompliancePoliceCertificate, deleteFile, getSignedUrl } from '../services/fileService';
import { useParams, Link } from 'react-router-dom';
import { FaCheckCircle, FaFileUpload, FaPaperclip, FaTimes, FaFileAlt, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileArchive, FaFileVideo, FaFileAudio, FaFileCode, FaCalendarAlt } from 'react-icons/fa';
import { fetchFileTypes, isCustomFileType, filterAttachmentFileTypes } from '../config/fileTypes';
import ConfirmationModal from '../components/ConfirmationModal';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ActivityLog from '../components/ActivityLog';
import { validateAbn, validateBsb, validateBankAccountNumber } from '../utils/franchiseeFieldValidators';

// Editing an existing franchisee must never be held hostage by a field the editor did not touch.
// 83 of the live records carry no BSB and 108 carry an ABN/BSB/account number that predates the
// format rules, all of them legitimately imported from Xero. Blocking those made a one-character
// name change impossible (ticket 86), so both rules below only bite on what the user actually did.

// Enforce a format rule only once the value differs from what was loaded.
const validateIfChanged = (validator, originalValue) => (value) => {
  const current = value == null ? '' : String(value);
  if (current === (originalValue == null ? '' : String(originalValue))) return true;
  return validator(current);
};

// Require a value only if the record already had one, so clearing a populated field is still
// caught while a record that arrived empty stays editable.
const requiredUnlessInitiallyEmpty = (originalValue, message) => (value) => {
  const wasEmpty = originalValue == null || String(originalValue).trim() === '';
  if (wasEmpty) return true;
  return String(value ?? '').trim() === '' ? message : true;
};

// Field name -> the label the editor actually sees, for the blocked-save summary.
const FIELD_LABELS = {
  franchiseCode: 'Franchise Code',
  franchiseName: 'Franchise Name',
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  phoneNumber: 'Phone Number',
  abn: 'ABN',
  gstRegistered: 'GST Registered',
  bsbNumber: 'BSB Number',
  bankAccountNumber: 'Bank Account Number',
  bankAccountName: 'Bank Account Name',
};

// Xero bank details verdict -> the one line the admin reads (ticket 100).
const XERO_BANK_VERDICTS = {
  'matching': { label: 'Xero: matching', className: 'text-green-700' },
  'different in Xero': { label: 'Xero: different', className: 'text-amber-700' },
  'missing in Xero': { label: 'Xero: not synced', className: 'text-amber-700' },
  'no Xero contact yet': { label: 'Xero: no contact yet', className: 'text-gray-500' },
  'bank details in Loope are incomplete': { label: 'Xero: bank details incomplete', className: 'text-gray-500' },
};
const XERO_BANK_UNKNOWN_VERDICT = { label: 'Xero status unavailable', className: 'text-gray-500' };

// Account numbers are never shown in full, only enough tail to tell two apart.
const accountTail = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits ? `ending ${digits.slice(-3)}` : 'nothing';
};

// At most one short sentence under the status line, and none when the two sides agree.
const describeXeroBankStatus = (status) => {
  switch (status.verdict) {
    case 'different in Xero':
      return `Xero holds an account ${accountTail(status.xeroHas)}, Loope would send one ${accountTail(status.wouldSend)}.`;
    case 'missing in Xero':
      return 'This Xero contact has no bank details yet.';
    case 'no Xero contact yet':
      return 'Create the Xero contact first, then the bank details can be sent.';
    case 'bank details in Loope are incomplete':
      return status.reason || 'Loope does not hold a BSB plus an account number.';
    default:
      return null;
  }
};

const EditFranchiseePage = () => {
  const { id: franchiseeId } = useParams();
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState(null);
  const [createdAt, setCreatedAt] = useState(null); // For the activity log's synthetic "created" row
  const [canUpdateFseCode, setCanUpdateFseCode] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState(null);

  // Save confirmation modal state
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [xeroContactIdWillChange, setXeroContactIdWillChange] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // File upload state
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, file: null });
  const [deleting, setDeleting] = useState(false);
  const [fileTypes, setFileTypes] = useState([]);
  const fileInputRef = useRef(null);

  // Compliance data state
  const [complianceData, setComplianceData] = useState(null);
  const [editableExpiryDate, setEditableExpiryDate] = useState('');
  // Track original expiry to detect changes for enabling Save Changes button
  const [savedExpiryDate, setSavedExpiryDate] = useState('');
  const [complianceFileUploading, setComplianceFileUploading] = useState(null); // null | 'police' | 'currency'
  const [complianceFileError, setComplianceFileError] = useState(null);
  const [complianceCertChanged, setComplianceCertChanged] = useState(false);
  const policeCertFileInputRef = useRef(null);
  const currencyFileInputRef = useRef(null);

  // Xero contact creation state
  const [xeroCreationLoading, setXeroCreationLoading] = useState(false);
  const [xeroCreationError, setXeroCreationError] = useState(null);
  const [xeroCreationSuccess, setXeroCreationSuccess] = useState(null);
  const [xeroPreviewLoading, setXeroPreviewLoading] = useState(false);
  const [xeroPreviewError, setXeroPreviewError] = useState(null);
  const [xeroPreviewModal, setXeroPreviewModal] = useState({ show: false, data: null });

  // Xero bank details state (ticket 100)
  const [xeroBankStatus, setXeroBankStatus] = useState(null);
  const [xeroBankResyncing, setXeroBankResyncing] = useState(false);
  const [xeroBankResyncError, setXeroBankResyncError] = useState(null);

  const mapFranchiseeToFormData = (data = {}) => ({
    franchiseCode: data.fseCode || '',
    franchiseName: data.franchiseName || '',
    email: data.email || '',
    phoneNumber: data.phoneNumber || '',
    abn: data.abn || '',
    gstRegistered: data.gstRegistered ? 'Yes' : 'No',
    country: data.country || 'Australia',
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    bsbNumber: data.bsbNumber || '',
    bankAccountNumber: data.bankAccountNumber || '',
    bankAccountName: data.bankAccountName || '',
    territory: data.territory || '',
    addressLine1: data.addressLine1 || '',
    addressLine2: data.addressLine2 || '',
    state: data.state || '',
    postalCode: data.postalCode || '',
    xeroContactId: data.xeroContactId || '',
    isExternal: Boolean(data.isExternal),
  });

  const { register, handleSubmit, reset, setValue, watch, control, formState: { errors, isSubmitting, isDirty } } = useForm({
    mode: 'onBlur',
    defaultValues: mapFranchiseeToFormData()
  });

  // A blocked save used to be invisible: the Save button sits in a fixed footer while the field
  // it is complaining about can be several screens up, so the page simply appeared to do nothing.
  const [blockedFields, setBlockedFields] = useState([]);
  const onInvalid = (formErrors) => {
    setBlockedFields(Object.entries(formErrors).map(([name, error]) => ({
      name,
      label: FIELD_LABELS[name] || name,
      message: error?.message || 'Please check this field.',
    })));
  };
  // Entries drop off as each field is re-validated on blur, so the summary empties as they are fixed.
  const outstandingBlockedFields = blockedFields.filter(({ name }) => errors[name]);
  const focusField = (name) => {
    const el = document.getElementById(name);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
  };

  // Payment details are optional to store but all three are needed before an ABA file can pay
  // this franchisee, so the gap is surfaced as a notice rather than a save block.
  const [bsbValue, accountNumberValue, accountNameValue] = watch(['bsbNumber', 'bankAccountNumber', 'bankAccountName']);
  const missingPaymentFields = [
    ['BSB', bsbValue],
    ['account number', accountNumberValue],
    ['account name', accountNameValue],
  ].filter(([, value]) => String(value ?? '').trim() === '').map(([label]) => label);

  // Xero being unreachable must never break the franchisee screen, so a failed read shows nothing.
  const loadXeroBankStatus = useCallback(async () => {
    if (!franchiseeId) return;
    try {
      setXeroBankStatus(await getXeroBankDetailsStatus(franchiseeId));
    } catch (error) {
      console.error('Could not read the Xero bank details status:', error);
      // A muted line, so an unreadable Xero is not mistaken for a franchisee with nothing to show.
      setXeroBankStatus({ verdict: 'unavailable' });
    }
  }, [franchiseeId]);

  useEffect(() => {
    const fetchFranchisee = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const data = await getFranchiseeById(franchiseeId);
        const formData = mapFranchiseeToFormData(data);
        setInitialData(formData);
        setCreatedAt(data.createdAt || null);
        setCanUpdateFseCode(data.canUpdateFseCode || false);
        reset(formData);
        
        // Load existing files if they exist
        if (data.attachments && Array.isArray(data.attachments)) {
          setUploadedFiles(data.attachments.map(file => ({
            id: file.id,
            name: file.fileName,
            url: file.url,
            size: file.size || 0,
            category: file.type || 'other', 
            customType: file.customType || '',
            expiryDate: file.expiryDate || ''
          })));
        } else {
          setUploadedFiles([]);
        }

        // Load compliance data - always initialize so the section is visible
        const complianceInfo = data.compliance || {};
        setComplianceData(complianceInfo);
        const expiry = complianceInfo.policeCertificateExpiryDate || '';
        setEditableExpiryDate(expiry);
        setSavedExpiryDate(expiry);

        loadXeroBankStatus();
      } catch (error) {
        setApiError(error.message || 'Failed to load franchisee details.');
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFranchisee();
  }, [franchiseeId, reset, loadXeroBankStatus]);

  // Load file types on component mount
  useEffect(() => {
    const loadFileTypes = async () => {
      try {
        const types = await fetchFileTypes()
        setFileTypes(filterAttachmentFileTypes(types));
      } catch (error) {
        console.error('Error loading file types:', error);
        // Set fallback values
        setFileTypes([{ value: 'other', label: 'Other' }]);
      } finally {
      }
    };

    loadFileTypes();
  }, []);

  useEffect(() => {
    let timer;
    if (saveSuccessMessage) {
      timer = setTimeout(() => {
        setSaveSuccessMessage(null);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [saveSuccessMessage]);

  const onSubmit = async (data) => {
    setBlockedFields([]); // validation passed, drop any summary left from an earlier attempt
    setApiError(null);
    setSaveSuccessMessage(null);

    const payload = { ...data };
    if (canUpdateFseCode && data.franchiseCode && data.franchiseCode !== initialData.franchiseCode) {
      payload.fseCode = data.franchiseCode;
    }
    delete payload.franchiseCode;
    delete payload.country;
    delete payload.compliance;
    delete payload.createdAt;
    delete payload.updatedAt;
    delete payload.status;
    if (!(canUpdateFseCode && data.franchiseCode && data.franchiseCode !== initialData.franchiseCode)) {
      delete payload.fseCode;
    }
    delete payload.id;

    if (data.xeroContactId) {
        payload.xeroContactId = data.xeroContactId;
    } else {
        delete payload.xeroContactId;
    }

    // Include file attachments
    payload.attachments = uploadedFiles.map(file => ({
      id: file.id,
      fileName: file.name,
      url: file.url,
      size: file.size,
      type: file.category || 'other',
      customType: file.customType || null,
      expiryDate: file.expiryDate || null
    }));

    // Detect if xeroContactId is changing
    const currentXeroContactId = initialData?.xeroContactId || '';
    const nextXeroContactId = data.xeroContactId || '';
    const xeroChanging = currentXeroContactId !== nextXeroContactId;

    setPendingSaveData(payload);
    setXeroContactIdWillChange(xeroChanging);
    setShowSaveConfirmModal(true);
  };

  const performActualSave = async () => {
    if (!pendingSaveData) return;
    setShowSaveConfirmModal(false);
    setIsSaving(true);
    setApiError(null);
    setSaveSuccessMessage(null);

    try {
      // Save franchisee fields and expiry date in parallel when expiry has changed
      const savePromises = [editFranchisee(franchiseeId, pendingSaveData)];
      if (editableExpiryDate !== savedExpiryDate || complianceCertChanged) {
        savePromises.push(
          updateFranchiseeCompliance(franchiseeId, { policeCertificateExpiryDate: editableExpiryDate || null })
            .then(() => {
              setComplianceData(prev => ({ ...prev, policeCertificateExpiryDate: editableExpiryDate }));
              setSavedExpiryDate(editableExpiryDate);
              setComplianceCertChanged(false);
            })
        );
      }
      const [updatedFranchisee] = await Promise.all(savePromises);
      const updatedFormData = mapFranchiseeToFormData(updatedFranchisee);
      reset(updatedFormData);
      setInitialData(updatedFormData);
      const xeroMsg = xeroContactIdWillChange
        ? ' Unpaid RCTIs are being updated with the new Xero Contact ID in the background.'
        : '';
      setSaveSuccessMessage(`Franchisee updated successfully!${xeroMsg}`);
      // A save pushes the bank details to Xero, so the status line would otherwise read stale.
      loadXeroBankStatus();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setApiError(error.message || 'Failed to update franchisee. Please try again.');
      console.error('Update error:', error);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSaving(false);
      setPendingSaveData(null);
      setXeroContactIdWillChange(false);
    }
  };

  const handleResyncXeroBankDetails = async () => {
    setXeroBankResyncing(true);
    setXeroBankResyncError(null);
    try {
      await syncXeroBankDetails(franchiseeId);
      await loadXeroBankStatus();
    } catch (error) {
      setXeroBankResyncError(error.message || 'Could not send the bank details to Xero.');
      console.error('Xero bank details resync failed:', error);
    } finally {
      setXeroBankResyncing(false);
    }
  };

  const xeroBankVerdict = xeroBankStatus ? (XERO_BANK_VERDICTS[xeroBankStatus.verdict] || XERO_BANK_UNKNOWN_VERDICT) : null;
  const xeroBankNote = xeroBankStatus ? describeXeroBankStatus(xeroBankStatus) : null;
  // Nothing to resend without a Xero contact, and nothing worth sending while Loope's own details are short.
  const xeroBankCanResync = Boolean(
    xeroBankStatus?.xeroContactId
    && xeroBankStatus.verdict !== 'matching'
    && xeroBankStatus.verdict !== 'bank details in Loope are incomplete'
  );

  const handlePreviewXeroContact = async () => {
    setXeroPreviewLoading(true);
    setXeroPreviewError(null);
    setXeroCreationError(null);
    setXeroCreationSuccess(null);

    try {
      const result = await previewXeroContactFromFranchisee(franchiseeId);
      setXeroPreviewModal({ show: true, data: result });
    } catch (error) {
      setXeroPreviewError(error.message || 'Failed to preview Xero contact');
    } finally {
      setXeroPreviewLoading(false);
    }
  };

  // Xero contact creation/linking handler
  const handleCreateXeroContact = async () => {
    setXeroCreationLoading(true);
    setXeroCreationError(null);
    setXeroCreationSuccess(null);

    try {
      const result = await createXeroContactFromFranchisee(franchiseeId);

      // Update the form with the new xeroContactId
      const updatedFormData = {
        ...initialData,
        xeroContactId: result.xeroContactId
      };
      reset(updatedFormData);
      setInitialData(updatedFormData);

      // Show different messages based on action and whether it was a sync
      const wasSync = initialData?.xeroContactId;
      const xeroIdChanged = initialData?.xeroContactId !== result.xeroContactId;

      if (wasSync) {
        if (xeroIdChanged) {
          setXeroCreationSuccess(`✓ Xero Contact ID updated in Loope (New ID: ${result.xeroContactId})`);
        } else {
          setXeroCreationSuccess(`✓ Xero contact verified - already in sync (ID: ${result.xeroContactId})`);
        }
      } else if (result.action === 'linked') {
        setXeroCreationSuccess(`✓ Linked to existing Xero contact (ID: ${result.xeroContactId})`);
      } else {
        setXeroCreationSuccess(`✓ Created new Xero contact (ID: ${result.xeroContactId})`);
      }

      // Clear success message after 5 seconds
      setTimeout(() => setXeroCreationSuccess(null), 5000);

      return true;
    } catch (error) {
      setXeroCreationError(error.message || 'Failed to process Xero contact');
      return false;
    } finally {
      setXeroCreationLoading(false);
    }
  };

  const handleCloseXeroPreviewModal = () => {
    setXeroPreviewModal({ show: false, data: null });
  };

  const formatXeroPhoneList = (phones = []) => {
    if (!phones.length) return '';
    const formatted = phones
      .map(phone => {
        const phoneType = phone.PhoneType || phone.phoneType || 'Phone';
        const phoneNumber = phone.PhoneNumber || phone.phoneNumber || '';
        return phoneNumber ? `${phoneType}: ${phoneNumber}` : '';
      })
      .filter(Boolean);
    return formatted.join(', ');
  };

  const formatXeroAddressList = (addresses = []) => {
    if (!addresses.length) return '';
    const formatted = addresses
      .map(address => {
        const parts = [
          address.AddressLine1 || address.addressLine1,
          address.AddressLine2 || address.addressLine2,
          address.City || address.city,
          address.Region || address.region,
          address.PostalCode || address.postalCode
        ].filter(Boolean);
        return parts.join(', ');
      })
      .filter(Boolean);
    return formatted.join(' | ');
  };

  const renderXeroPreviewField = (label, value) => (
    <div className="flex justify-between gap-4">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      <span className="text-sm text-gray-900 text-right break-all">{value || '—'}</span>
    </div>
  );

  // File upload functions
  const handleAttachFile = () => {
    fileInputRef.current.click();
  };

  // Check if files have changed from initial state
  const hasFileChanges = () => {
    if (!initialData) return false;
    
    const initialFiles = initialData.attachments || [];
    
    // Check if the number of files changed
    if (uploadedFiles.length !== initialFiles.length) return true;
    
    // Check if any file IDs are different
    const currentFileIds = uploadedFiles.map(f => f.id).sort();
    const initialFileIds = initialFiles.map(f => f.id).sort();
    
    return JSON.stringify(currentFileIds) !== JSON.stringify(initialFileIds);
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FaFilePdf className="text-red-500" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="text-blue-500" />;
      case 'xls':
      case 'xlsx':
        return <FaFileExcel className="text-green-500" />;
      case 'ppt':
      case 'pptx':
        return <FaFilePowerpoint className="text-orange-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
        return <FaFileImage className="text-purple-500" />;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return <FaFileArchive className="text-yellow-500" />;
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv':
        return <FaFileVideo className="text-pink-500" />;
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'aac':
        return <FaFileAudio className="text-indigo-500" />;
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
      case 'html':
      case 'css':
      case 'json':
      case 'xml':
        return <FaFileCode className="text-gray-600" />;
      default:
        return <FaFileAlt className="text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setUploadError(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return;
      }

      const newFile = {
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        category: fileTypes[0]?.value || 'other',
        customType: '',
        expiryDate: ''
      };

      console.log('newFile:', newFile)

      setAttachedFiles(prev => [...prev, newFile]);
    });

    console.log('attachedFiles:', attachedFiles)

    // Clear the input
    event.target.value = '';
    setUploadError(null);
  };

  const updateFileCategory = (fileId, category) => {
    setAttachedFiles(prev =>
      prev.map(file =>
        file.id === fileId ? { ...file, category, customType: category === 'other' ? file.customType : '' } : file
      )
    );
  };

  const updateFileCustomType = (fileId, customType) => {
    setAttachedFiles(prev =>
      prev.map(file =>
        file.id === fileId ? { ...file, customType } : file
      )
    );
  };

  const updateFileExpiryDate = (fileId, expiryDate) => {
    setAttachedFiles(prev =>
      prev.map(file =>
        file.id === fileId ? { ...file, expiryDate } : file
      )
    );
  };

  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleViewDocument = async (fileUrl) => {
    if (!fileUrl) return;
    try {
      const signedUrl = await getSignedUrl(fileUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening file:', err);
    }
  };

  const handlePoliceCertFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setComplianceFileUploading('police');
    setComplianceFileError(null);
    try {
      const result = await uploadCompliancePoliceCertificate(file);
      await updateFranchiseeCompliance(franchiseeId, { policeCertificate: result.url });
      setComplianceData(prev => ({ ...prev, policeCertificate: result.url }));
      setComplianceCertChanged(true);
      // Auto-fill expiry whenever the server extracts a date from the PDF
      if (result.expiryDate) {
        setEditableExpiryDate(result.expiryDate);
      }
    } catch (err) {
      setComplianceFileError('Failed to upload police certificate: ' + (err.message || 'Unknown error'));
    } finally {
      setComplianceFileUploading(null);
    }
  };

  const handleCurrencyFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setComplianceFileUploading('currency');
    setComplianceFileError(null);
    try {
      const result = await uploadSingleFile({ file, category: 'certificate-of-currency' });
      const url = result?.data?.urls?.[0] || result?.urls?.[0];
      if (!url) throw new Error('No URL returned from upload.');
      await updateFranchiseeCompliance(franchiseeId, { certificateOfCurrency: url });
      setComplianceData(prev => ({ ...prev, certificateOfCurrency: url }));
    } catch (err) {
      setComplianceFileError('Failed to upload certificate of currency: ' + (err.message || 'Unknown error'));
    } finally {
      setComplianceFileUploading(null);
    }
  };

  const removeUploadedFile = (fileId) => {
    const fileToDelete = uploadedFiles.find(file => file.id === fileId);
    if (fileToDelete) {
      setDeleteConfirmModal({ show: true, file: fileToDelete });
    }
  };

  const confirmDeleteFile = async () => {
    if (!deleteConfirmModal.file) return;
    
    setDeleting(true);
    try {
      await deleteFile(deleteConfirmModal.file.url);
      setUploadedFiles(prev => prev.filter(file => file.id !== deleteConfirmModal.file.id));
      setDeleteConfirmModal({ show: false, file: null });
    } catch (error) {
      console.error('Error deleting file:', error);
      setUploadError(error.message || 'Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteFile = () => {
    setDeleteConfirmModal({ show: false, file: null });
  };

  const handleUploadFiles = async () => {
    if (attachedFiles.length === 0) return;

    // Check if all files have required fields
    const filesWithoutCategory = attachedFiles.filter(file => !file.category.trim());
    if (filesWithoutCategory.length > 0) {
      setUploadError('Please select a file type for all files before uploading.');
      return;
    }

    const filesWithoutCustomType = attachedFiles.filter(file =>
      file.category === 'other' && !file.customType.trim()
    );
    if (filesWithoutCustomType.length > 0) {
      setUploadError('Please specify the custom type for "other" file types before uploading.');
      return;
    }

    const filesWithoutExpiryDate = attachedFiles.filter(file => !file.expiryDate);
    if (filesWithoutExpiryDate.length > 0) {
      setUploadError('Please provide an expiry date for all files before uploading.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const uploadData = attachedFiles.map(file => ({
        file: file.file,
        category: file.category,
        customType: file.customType,
        expiryDate: file.expiryDate,
        entityType: 'franchisee',
        entityId: franchiseeId
      }));

      const response = await uploadFiles(uploadData);
      
      // Handle the response structure - response.data.urls contains the URLs
      const urls = response.data?.urls || [];
      
      // Add uploaded files to the uploaded files list
      const newUploadedFiles = attachedFiles.map((file, index) => ({
        id: Date.now() + Math.random() + index,
        name: file.name,
        url: urls[index] || '',
        size: file.size || 0,
        category: file.category,
        customType: file.customType,
        expiryDate: file.expiryDate
      }));

      setUploadedFiles(prev => [...prev, ...newUploadedFiles]);
      setAttachedFiles([]); // Clear attached files
    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadError(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
    }
  };

  if (loading) {
    return <p className="text-center text-gray-500 py-10">Loading franchisee details...</p>;
  }

  return (
    <div className="container mx-auto px-4 pt-8 pb-20 max-w-3xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-jims-cyan-dark">Edit Franchisee</h1>
        <Link to="/franchisees" className="text-jims-cyan-dark hover:text-jims-cyan underline">
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} onKeyDown={handleKeyDown} className="bg-white p-8 rounded-lg shadow-md space-y-6">

        {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}
        {saveSuccessMessage && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded flex items-center">
            <FaCheckCircle className="mr-2" />
            {saveSuccessMessage}
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-1">
            <label htmlFor="franchiseCode" className="block text-sm font-medium text-gray-700">Franchise Code {canUpdateFseCode && '*'}</label>
            <div className="relative group">
              <input
                id="franchiseCode"
                type="text"
                {...register("franchiseCode", { required: "Franchise Code is required" })}
                readOnly={!canUpdateFseCode}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm ${
                  !canUpdateFseCode ? 'bg-gray-100 cursor-not-allowed border-gray-300' : 'border-gray-300 focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan'
                }`}
              />
              {!canUpdateFseCode && (
                <div className="absolute z-10 w-64 p-2 mt-1 text-sm text-white bg-gray-800 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200">
                  This FSE Code cannot be edited because it's already linked to existing jobs, invoices, or RCTIs. Use the FSE Code Update Utility instead.
                </div>
              )}
            </div>
          </div>
          <div className="col-span-2">
            <label htmlFor="franchiseName" className="block text-sm font-medium text-gray-700">Franchise Name *</label>
            <input
              id="franchiseName"
              type="text"
              {...register("franchiseName", { required: "Franchise Name is required." })}
              className={`mt-1 block w-full px-3 py-2 border ${errors.franchiseName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.franchiseName && <p className="mt-1 text-xs text-red-600">{errors.franchiseName.message}</p>}
          </div>
        </div>

        {/* External Franchisee Checkbox */}
        <div className="border-t pt-4 mt-4 border-gray-200">
            <div className="flex items-center">
                <input
                    type="checkbox"
                    id="isExternal"
                    {...register("isExternal")}
                    className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                />
                <label htmlFor="isExternal" className="ml-2 block text-sm text-gray-700">
                    External Franchisee
                </label>
                <span className="ml-2 text-xs text-gray-500">
                    (Check if this franchisee is external to the region)
                </span>
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name *</label>
            <input
              id="firstName"
              type="text"
              {...register("firstName", { validate: requiredUnlessInitiallyEmpty(initialData?.firstName, "First Name is required.") })}
              className={`mt-1 block w-full px-3 py-2 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>}
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name *</label>
            <input
              id="lastName"
              type="text"
              {...register("lastName", { validate: requiredUnlessInitiallyEmpty(initialData?.lastName, "Last Name is required.") })}
              className={`mt-1 block w-full px-3 py-2 border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email *</label>
            <input
              id="email"
              type="email"
              {...register("email", {
                required: "Email is required.",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address."
                }
              })}
              className={`mt-1 block w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
          </div>
          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Phone Number *</label>
            <input
              id="phoneNumber"
              type="tel"
              {...register("phoneNumber", { validate: requiredUnlessInitiallyEmpty(initialData?.phoneNumber, "Phone Number is required.") })}
              className={`mt-1 block w-full px-3 py-2 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.phoneNumber && <p className="mt-1 text-xs text-red-600">{errors.phoneNumber.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="abn" className="block text-sm font-medium text-gray-700">ABN *</label>
            <input
              id="abn"
              type="text"
              {...register("abn", {
                validate: {
                  present: requiredUnlessInitiallyEmpty(initialData?.abn, "ABN is required."),
                  format: validateIfChanged(validateAbn, initialData?.abn),
                },
              })}
              className={`mt-1 block w-full px-3 py-2 border ${errors.abn ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.abn && <p className="mt-1 text-xs text-red-600">{errors.abn.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">GST Registered *</label>
            <div className="mt-2 flex items-center space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="Yes"
                  {...register("gstRegistered", { required: "Please select GST registration status." })}
                  className="form-radio h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="No"
                  {...register("gstRegistered", { required: "Please select GST registration status." })}
                  className="form-radio h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
            </div>
            {errors.gstRegistered && <p className="mt-1 text-xs text-red-600">{errors.gstRegistered.message}</p>}
          </div>
        </div>

        {/* Payment details are optional to save, but incomplete ones stop an ABA payment run. */}
        {missingPaymentFields.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            Payment details incomplete, missing {missingPaymentFields.join(', ')}. You can still save
            these changes, but this franchisee cannot be paid by an ABA file until all three are set.
          </div>
        )}

        {/* BSB and Bank Account Number Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="bsbNumber" className="block text-sm font-medium text-gray-700">BSB Number</label>
            <input
              id="bsbNumber"
              type="text"
              {...register("bsbNumber", { validate: validateIfChanged(validateBsb, initialData?.bsbNumber) })}
              className={`mt-1 block w-full px-3 py-2 border ${errors.bsbNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.bsbNumber && <p className="mt-1 text-xs text-red-600">{errors.bsbNumber.message}</p>}
          </div>
          <div>
            <label htmlFor="bankAccountNumber" className="block text-sm font-medium text-gray-700">Bank Account Number</label>
            <input
              id="bankAccountNumber"
              type="text"
              {...register("bankAccountNumber", { validate: validateIfChanged(validateBankAccountNumber, initialData?.bankAccountNumber) })}
              className={`mt-1 block w-full px-3 py-2 border ${errors.bankAccountNumber ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
            />
            {errors.bankAccountNumber && <p className="mt-1 text-xs text-red-600">{errors.bankAccountNumber.message}</p>}
          </div>
        </div>

        {/* Bank Account Name Row */}
        <div>
          <label htmlFor="bankAccountName" className="block text-sm font-medium text-gray-700">Bank Account Name</label>
          <input
            id="bankAccountName"
            type="text"
            {...register("bankAccountName")}
            className={`mt-1 block w-full px-3 py-2 border ${errors.bankAccountName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm`}
          />
          {errors.bankAccountName && <p className="mt-1 text-xs text-red-600">{errors.bankAccountName.message}</p>}
        </div>

        {/* Does Xero hold the same bank details Loope would send (ticket 100). */}
        {xeroBankStatus && (
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2" aria-live="polite">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className={`text-sm font-medium ${xeroBankVerdict.className}`}>{xeroBankVerdict.label}</span>
              {xeroBankCanResync && (
                <button
                  type="button"
                  onClick={handleResyncXeroBankDetails}
                  disabled={xeroBankResyncing}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {xeroBankResyncing ? 'Resyncing...' : 'Resync to Xero'}
                </button>
              )}
            </div>
            {xeroBankNote && <p className="mt-1 text-xs text-gray-600">{xeroBankNote}</p>}
            {xeroBankResyncError && <p className="mt-1 text-xs text-red-600">{xeroBankResyncError}</p>}
          </div>
        )}

        {/* Xero Contact ID Field - always editable */}
        <div>
            <label htmlFor="xeroContactId" className="block text-sm font-medium text-gray-700">Xero Contact ID</label>
            <input
                id="xeroContactId"
                type="text"
                {...register("xeroContactId")}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                placeholder="Enter Xero Contact ID (optional)"
            />
        </div>

        {/* Xero Integration Section */}
        <div className="">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Xero Integration</h3>

          {!initialData?.xeroContactId && (
            <div className="mb-4">
              <button
                type="button"
                onClick={handlePreviewXeroContact}
                disabled={xeroPreviewLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-jims-cyan hover:bg-jims-cyan-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {xeroPreviewLoading ? 'Checking...' : 'Create in Xero'}
              </button>
              <p className="mt-2 text-sm text-gray-500">
                Creates a new Xero contact or links to an existing one if found (matched by Franchisee Code)
              </p>
            </div>
          )}

          {initialData?.xeroContactId && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center justify-between">
              <p className="text-sm text-green-800">
                <strong>Xero Contact ID:</strong> {initialData.xeroContactId}
              </p>
              <button
                type="button"
                onClick={handlePreviewXeroContact}
                disabled={xeroPreviewLoading}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-jims-cyan hover:bg-jims-cyan-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {xeroPreviewLoading ? 'Checking...' : 'Sync'}
              </button>
            </div>
          )}

          {xeroCreationError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{xeroCreationError}</p>
            </div>
          )}

          {xeroPreviewError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{xeroPreviewError}</p>
            </div>
          )}

          {xeroCreationSuccess && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800">{xeroCreationSuccess}</p>
            </div>
          )}
        </div>

        <div className="border-t pt-6 mt-6 border-gray-200">
          <div className="mb-6">
            <label htmlFor="territory" className="block text-sm font-medium text-gray-700">Territory</label>
            <input
              id="territory"
              type="text"
              {...register("territory")}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AddressAutocomplete
              control={control}
              setValue={setValue}
            />
            <div>
              <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700">Address Line 2</label>
              <input
                id="addressLine2"
                type="text"
                {...register("addressLine2")}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="state" className="block text-sm font-medium text-gray-700">State</label>
              <input
                id="state"
                type="text"
                {...register("state")}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">Postal Code</label>
              <input
                id="postalCode"
                type="text"
                {...register("postalCode")}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700">Country</label>
              <input
                id="country"
                type="text"
                {...register("country")}
                readOnly
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm bg-gray-100 cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {/* File Upload Section */}
        <div className="border-t pt-6 mt-6 border-gray-200">
          <p className="text-sm text-gray-600 mb-3">File Attachments:</p>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="*/*"
          />

          {/* Attach File Link */}
          <div className="mb-4">
            <button
              type="button"
              onClick={handleAttachFile}
              className="inline-flex items-center text-jims-cyan hover:text-jims-cyan-dark font-medium text-sm"
            >
              <FaPaperclip className="mr-2" />
              Attach File
            </button>
            <span className="ml-2 text-xs text-gray-500">(Max 10MB per file)</span>
          </div>

          {/* Display attached files (to be uploaded) */}
          {attachedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Files to Upload:</h4>
              {attachedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        File Type *
                      </label>
                      <select
                        value={file.category}
                        onChange={(e) => updateFileCategory(file.id, e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan"
                      >
                        {fileTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {isCustomFileType(file.category) && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Custom Type *
                        </label>
                        <input
                          type="text"
                          value={file.customType}
                          onChange={(e) => updateFileCustomType(file.id, e.target.value)}
                          placeholder="Specify the document type"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Expiry Date *
                      </label>
                      <input
                        type="date"
                        value={file.expiryDate}
                        onChange={(e) => updateFileExpiryDate(file.id, e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Upload Files Button - right after files to upload */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleUploadFiles}
                  disabled={uploading}
                  className="inline-flex items-center px-4 py-2 bg-jims-cyan text-white text-sm font-medium rounded-md hover:bg-jims-cyan-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaFileUpload className="mr-2" />
                  {uploading ? 'Uploading...' : `Upload ${attachedFiles.length} File(s)`}
                </button>
              </div>
            </div>
          )}

          {/* Display uploaded files */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Uploaded Files:</h4>
              {uploadedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => handleViewDocument(file.url)}
                          className="text-sm font-medium text-jims-cyan hover:text-jims-cyan-dark underline truncate block text-left"
                          title="Click to download file"
                        >
                          {file.name}
                        </button>
                        <p className="text-xs text-gray-500">
                          {file.size > 0 ? formatFileSize(file.size) : 'Size unknown'} •
                          Type: {file.category === 'other' && file.customType ? file.customType : fileTypes.find(type => type.value === file.category)?.label}
                          {file.expiryDate && ` • Expires: ${new Date(file.expiryDate).toLocaleDateString('en-AU')}`}
                        </p>
                        <p className="text-xs text-green-600 font-medium">
                          ✓ Uploaded successfully
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUploadedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded-md">
              {uploadError}
            </div>
          )}
        </div>

        {/* Confirmation Modal for File Deletion */}
        {deleteConfirmModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirm File Deletion
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete "{deleteConfirmModal.file?.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {xeroPreviewModal.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
            <div className="relative mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {initialData?.xeroContactId
                      ? 'Sync Xero Contact'
                      : (xeroPreviewModal.data?.exists ? 'Existing Xero Contact Found' : 'Create New Xero Contact')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {initialData?.xeroContactId
                      ? 'Verify the Xero contact matches this franchisee.'
                      : (xeroPreviewModal.data?.exists
                        ? 'This franchisee already exists in Xero. The Xero Contact ID will be updated in Loope to link both systems.'
                        : 'No matching contact was found in Xero. Review what will be created.')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCloseXeroPreviewModal}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="mt-6 space-y-3">
                {xeroPreviewModal.data?.exists && xeroPreviewModal.data?.existingContact && (
                  <div className="space-y-2">
                    {renderXeroPreviewField('Contact ID', xeroPreviewModal.data.existingContact.ContactID)}
                    {renderXeroPreviewField('Name', xeroPreviewModal.data.existingContact.Name)}
                    {renderXeroPreviewField('Account Number', xeroPreviewModal.data.existingContact.AccountNumber)}
                    {renderXeroPreviewField('Email', xeroPreviewModal.data.existingContact.EmailAddress)}
                    {renderXeroPreviewField('Phones', formatXeroPhoneList(xeroPreviewModal.data.existingContact.Phones))}
                    {renderXeroPreviewField('Addresses', formatXeroAddressList(xeroPreviewModal.data.existingContact.Addresses))}
                  </div>
                )}

                {/* Sync status indicator when IDs don't match */}
                {initialData?.xeroContactId && xeroPreviewModal.data?.exists &&
                 xeroPreviewModal.data?.existingContact?.ContactID !== initialData.xeroContactId && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> The Xero Contact ID in Loope ({initialData.xeroContactId}) differs from the one found in Xero ({xeroPreviewModal.data.existingContact.ContactID}).
                      Clicking "Confirm Sync" will update Loope with the correct ID.
                    </p>
                  </div>
                )}

                {/* Already in sync indicator */}
                {initialData?.xeroContactId && xeroPreviewModal.data?.exists &&
                 xeroPreviewModal.data?.existingContact?.ContactID === initialData.xeroContactId && (
                  <div className="rounded-md bg-green-50 border border-green-200 p-3">
                    <p className="text-sm text-green-800">
                      <strong>✓ In Sync:</strong> The Xero Contact ID in Loope matches the one in Xero.
                    </p>
                  </div>
                )}

                {!xeroPreviewModal.data?.exists && xeroPreviewModal.data?.wouldCreateContact && (
                  <div className="space-y-2">
                    {renderXeroPreviewField('Name', xeroPreviewModal.data.wouldCreateContact.Name)}
                    {renderXeroPreviewField('First Name', xeroPreviewModal.data.wouldCreateContact.FirstName)}
                    {renderXeroPreviewField('Last Name', xeroPreviewModal.data.wouldCreateContact.LastName)}
                    {renderXeroPreviewField('Account Number', xeroPreviewModal.data.wouldCreateContact.AccountNumber)}
                    {renderXeroPreviewField('Email', xeroPreviewModal.data.wouldCreateContact.EmailAddress)}
                    {renderXeroPreviewField('ABN', xeroPreviewModal.data.wouldCreateContact.TaxNumber)}
                    {renderXeroPreviewField('Phone', formatXeroPhoneList(xeroPreviewModal.data.wouldCreateContact.Phones))}
                    {renderXeroPreviewField('Bank Account', xeroPreviewModal.data.wouldCreateContact.BankAccountDetails)}
                  </div>
                )}

                {!!xeroPreviewModal.data?.missingFields?.length && (
                  <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3">
                    <p className="text-sm text-yellow-800">
                      Missing required fields: {xeroPreviewModal.data.missingFields.join(', ')}.
                      Please update the franchisee record before linking.
                    </p>
                  </div>
                )}

                {xeroCreationError && (
                  <div className="rounded-md bg-red-50 border border-red-200 p-3">
                    <p className="text-sm text-red-800">{xeroCreationError}</p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseXeroPreviewModal}
                  disabled={xeroCreationLoading}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const success = await handleCreateXeroContact();
                    if (success) {
                      handleCloseXeroPreviewModal();
                    }
                  }}
                  disabled={xeroCreationLoading || xeroPreviewModal.data?.canCreate === false}
                  className="px-4 py-2 bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {xeroCreationLoading
                    ? (initialData?.xeroContactId ? 'Syncing...' : 'Linking...')
                    : (initialData?.xeroContactId
                        ? 'Confirm Sync'
                        : (xeroPreviewModal.data?.exists ? 'Link to Existing' : 'Create Contact'))}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Compliance Information Section */}
        {complianceData && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Compliance Information</h3>

            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              {/* Police Certificate */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Police Check Certificate</h4>
                <input
                  ref={policeCertFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={handlePoliceCertFileChange}
                />
                <div className="space-y-2">
                  {complianceData.policeCertificate ? (
                    <div className="flex items-center space-x-2">
                      <FaFilePdf className="text-red-500" />
                      <button
                        type="button"
                        onClick={() => handleViewDocument(complianceData.policeCertificate)}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        View Police Certificate
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No police certificate uploaded</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => policeCertFileInputRef.current?.click()}
                      disabled={complianceFileUploading === 'police'}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 transition duration-150"
                    >
                      <FaFileUpload className="text-gray-500" />
                      {complianceFileUploading === 'police' ? 'Uploading...' : complianceData.policeCertificate ? 'Replace File' : 'Upload File'}
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <FaCalendarAlt className="text-gray-500 flex-shrink-0" />
                    <label className="text-sm text-gray-600 flex-shrink-0">Expiry date:</label>
                    <input
                      type="date"
                      value={editableExpiryDate}
                      onChange={(e) => setEditableExpiryDate(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan"
                    />
                    {editableExpiryDate && new Date(editableExpiryDate) < new Date() && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                        Expired
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Certificate of Currency */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Certificate of Currency</h4>
                <input
                  ref={currencyFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf,image/*"
                  className="hidden"
                  onChange={handleCurrencyFileChange}
                />
                <div className="space-y-2">
                  {complianceData.certificateOfCurrency ? (
                    <>
                      <div className="flex items-center space-x-2">
                        <FaFilePdf className="text-red-500" />
                        <button
                          type="button"
                          onClick={() => handleViewDocument(complianceData.certificateOfCurrency)}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          View Certificate of Currency
                        </button>
                      </div>
                      {(complianceData.certificateOfCurrencyStartDate || complianceData.certificateOfCurrencyFinishDate) && (
                        <div className="flex items-center space-x-2 text-sm">
                          <FaCalendarAlt className="text-gray-500" />
                          <span className="text-gray-600">
                            {complianceData.certificateOfCurrencyStartDate && (
                              <>Start: {new Date(complianceData.certificateOfCurrencyStartDate).toLocaleDateString('en-AU')}</>
                            )}
                            {complianceData.certificateOfCurrencyStartDate && complianceData.certificateOfCurrencyFinishDate && (
                              <> | </>
                            )}
                            {complianceData.certificateOfCurrencyFinishDate && (
                              <>Finish: {new Date(complianceData.certificateOfCurrencyFinishDate).toLocaleDateString('en-AU')}</>
                            )}
                          </span>
                          {complianceData.certificateOfCurrencyFinishDate && new Date(complianceData.certificateOfCurrencyFinishDate) < new Date() && (
                            <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                              Expired
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No certificate of currency uploaded</p>
                  )}
                  <button
                    type="button"
                    onClick={() => currencyFileInputRef.current?.click()}
                    disabled={complianceFileUploading === 'currency'}
                    className="inline-flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 transition duration-150"
                  >
                    <FaFileUpload className="text-gray-500" />
                    {complianceFileUploading === 'currency' ? 'Uploading...' : complianceData.certificateOfCurrency ? 'Replace File' : 'Upload File'}
                  </button>
                </div>
              </div>

              {/* Compliance file upload error */}
              {complianceFileError && (
                <p className="text-xs text-red-600 -mt-2">{complianceFileError}</p>
              )}

              {/* Influenza Vaccine Declaration */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Influenza Vaccine Declaration</h4>
                {complianceData.hasInfluenzaVaccine ? (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <FaCheckCircle className="text-green-500" />
                      <span className="text-sm text-gray-700">Declared received influenza vaccine</span>
                    </div>
                    {complianceData.influenzaVaccineDate && (
                      <div className="flex items-center space-x-2 text-sm ml-6">
                        <FaCalendarAlt className="text-gray-500" />
                        <span className="text-gray-600">
                          Date: {new Date(complianceData.influenzaVaccineDate).toLocaleDateString('en-AU')}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No declaration made</p>
                )}
              </div>

              {/* LCHS Fact Sheet Acknowledgment */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">LCHS Aged Care Provider Training</h4>
                {complianceData.hasReadLCHSFactSheet ? (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <FaCheckCircle className="text-green-500" />
                      <span className="text-sm text-gray-700">Acknowledged reading LCHS fact sheet</span>
                    </div>
                    {complianceData.lchsFactSheetDate && (
                      <div className="flex items-center space-x-2 text-sm ml-6">
                        <FaCalendarAlt className="text-gray-500" />
                        <span className="text-gray-600">
                          Date: {new Date(complianceData.lchsFactSheetDate).toLocaleDateString('en-AU')}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No acknowledgment made</p>
                )}
              </div>
            </div>
          </div>
        )}

         <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              {outstandingBlockedFields.length > 0 && (
                <div role="alert" className="container mx-auto px-6 pt-3 max-w-3xl">
                  <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                    <p className="font-semibold">
                      Not saved. {outstandingBlockedFields.length === 1 ? 'One field needs' : `${outstandingBlockedFields.length} fields need`} attention:
                    </p>
                    <ul className="mt-1 list-disc list-inside space-y-0.5">
                      {outstandingBlockedFields.map(({ name, label, message }) => (
                        <li key={name}>
                          <button
                            type="button"
                            onClick={() => focusField(name)}
                            className="underline underline-offset-2 hover:no-underline focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                          >
                            {label}
                          </button>
                          {`: ${message}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <div className="container mx-auto px-6 py-3 max-w-3xl flex justify-end gap-3">
          <Link to="/franchisees" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded transition duration-300">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || isSaving || (!isDirty && !hasFileChanges() && editableExpiryDate === savedExpiryDate && !complianceCertChanged)}
            className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-300 disabled:opacity-50"
          >
            {isSubmitting || isSaving ? 'Saving...' : 'Save Changes'}
          </button>
          </div>
        </div>
      </form>

      {!loading && (
        <ActivityLog
          entityType="franchisee"
          entityId={franchiseeId}
          createdAt={createdAt}
          createdLabel="Franchisee created"
        />
      )}

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSaveConfirmModal}
        onClose={() => setShowSaveConfirmModal(false)}
        onConfirm={performActualSave}
        title="Confirm Save Changes"
        message={
          xeroContactIdWillChange
            ? 'Are you sure you want to save these changes? The Xero Contact ID is being updated — all unpaid RCTIs for this franchisee will also be updated with the new Xero Contact ID.'
            : 'Are you sure you want to save these changes?'
        }
      />
    </div>
  );
};

export default EditFranchiseePage;
